import { useEffect, useRef, useState, useMemo } from 'react';
import { api } from '../lib/api';
import { useServicesStore } from '../stores/servicesStore';
import { slugify } from '../lib/slug';

export default function AIAssistant({ title = 'AI Assistant', threadId = 'default', persona = 'buyer', pageName = '/', shortReplyPolicy = true, loggedIn = false, userId = null }) {
  const userKey = loggedIn ? (userId || 'user') : 'anon';
  const [activeThreadId, setActiveThreadId] = useState(threadId || 'default');
  const chatStorageKey = `ai:chat:${userKey}:${activeThreadId}`;
  const systemPrompt = useMemo(() => {
    const roleLine = persona === 'talent' ? 'You assist Creative users (talent).' : 'You assist Client users.';
    const pageHint = pageName ? `Current page: ${pageName}.` : '';
    const brevity = shortReplyPolicy ? 'Keep every answer very short (2–3 lines). If the user asks to continue or to provide more detail to a previous answer, politely decline and do not expand.' : 'Answer concisely.';
    return `${roleLine} ${pageHint} ${brevity}`.trim();
  }, [persona, pageName, shortReplyPolicy]);
  const [messages, setMessages] = useState([
    { role: 'system', content: 'You are a helpful assistant for Kreativ. Answer concisely.' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);
  const [voiceActive, setVoiceActive] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [threads, setThreads] = useState([]);
  // Access dynamic categories from services store (no hardcoding)
  const categoriesList = useServicesStore((s) => s.categoriesList);
  const fetchCategories = useServicesStore((s) => s.fetchCategories);
  const triedFetchCatsRef = useRef(false);
  const suggestions = useMemo(() => {
    if (persona === 'talent') {
      return [
        'Show tasks due today briefly.',
        'Suggest updates to improve my portfolio quickly.',
        'Any new messages needing my reply?',
      ];
    }
    // default buyer
    return [
      'Summarize open orders in 2 lines.',
      'Suggest next action for my latest booking.',
      'Any unread messages to check now?',
    ];
  }, [persona]);

  useEffect(() => {
    // Load thread list (only for logged in users)
    if (loggedIn) {
      try {
        const rawThreads = localStorage.getItem(`ai:threads:${userKey}`);
        const savedThreads = rawThreads ? JSON.parse(rawThreads) : [];
        if (Array.isArray(savedThreads) && savedThreads.length > 0) {
          setThreads(savedThreads);
          // validate current thread exists
          const exists = savedThreads.some(t => t.id === activeThreadId);
          if (!exists) setActiveThreadId(savedThreads[0].id);
        } else {
          const initial = [{ id: activeThreadId || 'default', title: 'Chat', createdAt: Date.now() }];
          setThreads(initial);
          localStorage.setItem(`ai:threads:${userKey}`, JSON.stringify(initial));
        }
      } catch (e) { void e; }
    }
    // Load saved messages for this thread
    try {
      const raw = localStorage.getItem(chatStorageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (Array.isArray(saved) && saved.length > 0) setMessages(saved);
      }
    } catch (e) { void e; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThreadId, userKey, loggedIn]);
  // Synchronize the first system message with current policy/persona
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 0) return [{ role: 'system', content: systemPrompt }];
      const next = [...prev];
      if (next[0]?.role === 'system') {
        next[0] = { role: 'system', content: systemPrompt };
        return next;
      }
      return [{ role: 'system', content: systemPrompt }, ...next];
    });
  }, [systemPrompt]);

  // Voice recognition (browser-based)
  useEffect(() => {
    const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
    setVoiceSupported(!!SpeechRec);
  }, []);

  // Lazy-load categories for intent parsing (public endpoint)
  useEffect(() => {
    const hasCats = Array.isArray(categoriesList) && categoriesList.length > 0;
    if (!hasCats && !triedFetchCatsRef.current) {
      triedFetchCatsRef.current = true;
      try { fetchCategories?.(); } catch (e) { void e; }
    }
  }, [categoriesList, fetchCategories]);

  function startVoice() {
    const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRec) { setError('Voice input not supported in this browser.'); return; }
    try {
      const rec = new SpeechRec();
      rec.lang = (navigator.language || 'en-US');
      rec.interimResults = false;
      rec.maxAlternatives = 1;
      rec.onstart = () => setVoiceActive(true);
      rec.onend = () => setVoiceActive(false);
      rec.onerror = (e) => { setError(e?.error || 'Voice error'); setVoiceActive(false); };
      rec.onresult = (event) => {
        const transcript = Array.from(event.results).map(r => r[0]?.transcript || '').join(' ').trim();
        if (transcript) {
          setInput(transcript);
          // Auto-send for quick commands
          sendMessage();
        }
      };
      rec.start();
    } catch (e) {
      setError(e?.message || 'Failed to start voice');
    }
  }

  // Simple command parser for navigation intents
  function parseCommand(text) {
    const s = String(text || '').toLowerCase();
    const normalized = s.replace(/\bdesigners?\b/g, 'design');
    const hasMap = /map/.test(s);
    // Broaden verbs/phrases
    const verbs = '(find|show|browse|search|look\\s*for|looking\\s*for|need|hire|book|discover|locate|get|want|want\\s*to\\s*find)';
    // Broaden roles/skills with synonyms
    const roles = '(creative|talent|designer|graphic\\s*designer|illustrator|animator|motion\\s*designer|3d\\s*artist|developer|frontend|backend|full\\s*stack|photographer|photography|filmmaker|videographer|video\\s*editor|editor|writer|copywriter|content\\s*writer|journalist|blogger|marketer|digital\\s*marketer|branding|brand|brand\\s*strategist|web\\s*design|ui|ux|ui\\s*ux|product\\s*designer|logo\\s*designer|seo|sem|social\\s*media|copywriting|art\\s*director|creative\\s*director)';
    const findCreatives = new RegExp(`${verbs}.*?${roles}`).test(s);
    const findServices = new RegExp(`${verbs}.*?(service|services|offer|offering|package|packages)`).test(s);
    const hasFind = /(find|search|look\s*for|need|hire)/.test(s);
    const cityMatch = s.match(/\b(?:in)\s+([a-zA-ZÅÄÖåäö\-\s]+)/);
    const logoDesign = /logo\s*design/.test(s);
    const skillMatch = s.match(new RegExp(`\\b${roles}\\b`));
    const shortBarePhrase = /[a-zA-ZÅÄÖåäö\-\s]{3,}/.test(s) && s.trim().split(/\s+/).length <= 4 && !/[?.!]/.test(s);
    const base = '/services/allservices';
    const onCreatives = String(pageName || '').startsWith('/creatives');
    const onAllServices = String(pageName || '').startsWith('/services/allservices');
    const onServices = !onAllServices && String(pageName || '').startsWith('/services');

    // If already on Creatives page, prefer applying creatives filters in-place
    if (onCreatives && !findServices) {
      const params = new URLSearchParams();
      if (skillMatch) {
        params.set('q', skillMatch[0]);
      } else if (hasFind || shortBarePhrase) {
        const cleaned = s.replace(/[^a-zA-ZÅÄÖåäö\-\s]/g, ' ').trim();
        if (cleaned) params.set('q', cleaned);
      } else {
        // Fallback: use the entire cleaned query if no specific patterns match
        const cleaned = s.replace(/[^a-zA-ZÅÄÖåäö\-\s]/g, ' ').trim();
        if (cleaned) params.set('q', cleaned);
      }
      if (cityMatch) params.set('city', cityMatch[1].trim());
      return { type: 'navigate', path: '/creatives', search: params.toString() ? `?${params.toString()}` : '' };
    }

    // If on Services or AllServices, prefer applying services search in-place
    if ((onServices || onAllServices) && !hasMap) {
      const q = skillMatch ? skillMatch[0] : (hasFind || shortBarePhrase ? s.replace(/[^a-zA-ZÅÄÖåäö\-\s]/g, ' ').trim() : '');
      const params = q ? `/q/${encodeURIComponent(q.replace(/\s+/g, '-'))}` : '';
      return { type: 'navigate', path: `${base}${params}`, search: '' };
    }
    // Dynamic category detection from store (no hardcoded list)
    try {
      const list = Array.isArray(categoriesList) ? categoriesList : [];
      if (list.length > 0) {
        const categorySlugs = [];
        list.forEach((c) => {
          const nm = (c?.name || c?.title || '').trim();
          if (nm) categorySlugs.push(slugify(nm));
          const subs = Array.isArray(c?.subcategories) ? c.subcategories : [];
          subs.forEach((sc) => {
            const sn = (sc?.name || sc?.title || '').trim();
            if (sn) categorySlugs.push(slugify(sn));
          });
        });
        const textTokens = normalized.replace(/[^a-zA-ZÅÄÖåäö\-\s]/g, ' ').split(/\s+/).filter(Boolean);
        const normalizeWord = (w) => {
          let x = w;
          x = x.replace(/ers$/,'er');
          x = x.replace(/ists$/,'ist');
          x = x.replace(/s$/,'');
          x = x.replace(/ing$/,'');
          x = x.replace(/er$/,'');
          return x;
        };
        const tokensToSlug = (arr) => slugify(arr.map(normalizeWord).join(' '));
        const slugToTokens = (slug) => slug.split('-').filter(Boolean);
        const tokensSet = (arr) => new Set(arr);
        let directMatchSlug = null;
        for (let len = Math.min(3, textTokens.length); len >= 1 && !directMatchSlug; len--) {
          for (let i = 0; i + len <= textTokens.length && !directMatchSlug; i++) {
            const phraseTokens = textTokens.slice(i, i + len);
            const testSlug = tokensToSlug(phraseTokens);
            if (categorySlugs.includes(testSlug)) {
              directMatchSlug = testSlug;
            }
          }
        }
        if (directMatchSlug) {
          return { type: 'navigate', path: `${base}/categories/${directMatchSlug}`, search: '' };
        }
        // Fuzzy: choose category whose tokens are a superset of phrase tokens (e.g., 'ui designer' -> 'ui-ux-design')
        let bestSlug = null;
        let bestScore = 0;
        for (let len = Math.min(3, textTokens.length); len >= 1; len--) {
          for (let i = 0; i + len <= textTokens.length; i++) {
            const phraseTokens = textTokens.slice(i, i + len).map(normalizeWord);
            const phraseSet = tokensSet(slugToTokens(slugify(phraseTokens.join(' '))));
            if (phraseSet.size === 0) continue;
            for (const catSlug of categorySlugs) {
              const catSet = tokensSet(slugToTokens(catSlug));
              // score: proportion of phrase tokens present in category tokens
              let present = 0;
              phraseSet.forEach((t) => { if (catSet.has(t)) present++; });
              const score = present / phraseSet.size;
              if (score > bestScore && score >= 0.66) { // require at least 2/3 overlap
                bestScore = score;
                bestSlug = catSlug;
              }
            }
          }
          if (bestSlug) break;
        }
        if (bestSlug) {
          return { type: 'navigate', path: `${base}/categories/${bestSlug}`, search: '' };
        }
      }
    } catch (e) { void e; }
    // Prefer creatives when user uses a seeking verb and mentions a skill (e.g., "find writer for me")
    if (hasFind && skillMatch && !findServices) {
      const params = new URLSearchParams();
      params.set('q', skillMatch[0]);
      if (cityMatch) params.set('city', cityMatch[1].trim());
      return { type: 'navigate', path: '/creatives', search: `?${params.toString()}` };
    }
    // Services intent
    if (findServices || logoDesign) {
      // Prefer category segment when we detect specific service
      if (logoDesign) {
        // No dedicated category for logo; use search query
        return { type: 'navigate', path: `${base}/q/logo-design`, search: '' };
      }
      const params = [];
      if (skillMatch) params.push('q', encodeURIComponent(skillMatch[0].replace(/\s+/g, '-')));
      const path = base + (params.length ? `/${params[0]}/${params[1]}` : '');
      return { type: 'navigate', path, search: '' };
    }
    // Creatives intent
    if (hasMap || findCreatives) {
      const path = hasMap ? '/creatives/map' : '/creatives';
      const params = new URLSearchParams();
      if (skillMatch) params.set('q', skillMatch[0]);
      else if (shortBarePhrase) {
        const cleaned = s.replace(/[^a-zA-ZÅÄÖåäö\-\s]/g, ' ').trim();
        if (cleaned) params.set('q', cleaned);
      }
      if (cityMatch) params.set('city', cityMatch[1].trim());
      return { type: 'navigate', path, search: params.toString() ? `?${params.toString()}` : '' };
    }
    // Fallback: determine if query is more likely for creatives or services
    if (logoDesign || skillMatch || shortBarePhrase) {
      // If we're already on creatives page or query suggests looking for people, go to creatives
      if (onCreatives || /\b(freelancer|designer|developer|writer|artist|creative|talent|person|people|someone|expert)\b/i.test(s)) {
        const params = new URLSearchParams();
        const query = skillMatch ? skillMatch[0] : s.replace(/[^a-zA-ZÅÄÖåäö\-\s]/g, ' ').trim();
        if (query) params.set('q', query);
        if (cityMatch) params.set('city', cityMatch[1].trim());
        return { type: 'navigate', path: '/creatives', search: params.toString() ? `?${params.toString()}` : '' };
      }
      // Otherwise, default to services
      const q = encodeURIComponent(s.trim().replace(/\s+/g, '-'));
      return { type: 'navigate', path: `${base}/q/${q}`, search: '' };
    }
    return null;
  }
  useEffect(() => {
    try {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    } catch (e) { void e; }
    // Persist messages per thread
    try { localStorage.setItem(chatStorageKey, JSON.stringify(messages)); } catch (e) { void e; }
  }, [messages, chatStorageKey]);

  async function sendMessage(e) {
    e?.preventDefault?.();
    const content = input.trim();
    if (!content) return;
    setError('');
    // Commands: navigate for anonymous buyer intent
    const command = parseCommand(content);
    if (command && command.type === 'navigate') {
      try {
        window.dispatchEvent(new CustomEvent('kreativ:navigate', { detail: { path: command.path, search: command.search } }));
        const confirm = `Navigating to ${command.path}${command.search || ''}…`;
        const nextConfirm = [...messages, { role: 'user', content }, { role: 'assistant', content: shortReplyPolicy ? confirm.slice(0, 140) : confirm }];
        setMessages(nextConfirm);
        setInput('');
        return;
      } catch (e) { void e;
        // fall through to normal AI processing if navigation fails
      }
    }
    // Policy: do not expand when asked to continue or add details
    const lower = content.toLowerCase();
    const asksToContinue = /(continue|more detail|details|expand|elaborate)/.test(lower);
    if (shortReplyPolicy && asksToContinue) {
      const refusal = 'I keep answers brief (2–3 lines) and won’t expand further.';
      const nextRefusal = [...messages, { role: 'user', content }, { role: 'assistant', content: refusal }];
      setMessages(nextRefusal);
      setInput('');
      return;
    }
    // If not logged in, avoid calling backend AI and respond locally
    if (!loggedIn) {
      const note = 'Log in to use AI chat. You can still navigate using commands.';
      const nextLocal = [...messages, { role: 'user', content }, { role: 'assistant', content: shortReplyPolicy ? note : note }];
      setMessages(nextLocal);
      setInput('');
      return;
    }
    const baseSystem = { role: 'system', content: systemPrompt };
    const history = messages.filter(m => m.role !== 'system');
    const next = [baseSystem, ...history, { role: 'user', content }];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      const res = await api.post('/ai/chat', { messages: next });
      let reply = res?.data?.data?.reply || 'No reply';
      if (shortReplyPolicy) {
        const sentences = reply.split(/(?<=[.!?])\s+/).slice(0, 3);
        reply = sentences.join(' ');
      }
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to get AI reply');
    } finally {
      setLoading(false);
    }
  }

  function applySuggestion(text, autoSend = true) {
    setInput(text);
    if (autoSend) {
      // simulate submit
      sendMessage();
    }
  }

  function newChat() {
    if (!loggedIn) return; // threads only for logged in
    const id = String(Date.now());
    const meta = { id, title: 'Chat', createdAt: Date.now() };
    const nextThreads = [...threads, meta];
    setThreads(nextThreads);
    try { localStorage.setItem(`ai:threads:${userKey}`, JSON.stringify(nextThreads)); } catch (e) { void e; }
    setActiveThreadId(id);
    setMessages([{ role: 'system', content: systemPrompt }]);
  }

  return (
    <div className="card">
      <div className="font-medium mb-2 flex items-center justify-between">
        <span>{title}</span>
        {loggedIn && (
          <div className="flex items-center gap-2">
            <select
              className="border rounded-md text-xs px-2 py-1"
              value={activeThreadId}
              onChange={(e) => setActiveThreadId(e.target.value)}
            >
              {threads.map((t) => (
                <option key={t.id} value={t.id}>{t.title || 'Chat'}</option>
              ))}
            </select>
            <button className="btn btn-outline text-xs" onClick={newChat}>New chat</button>
          </div>
        )}
      </div>
      <div
        ref={scrollRef}
        className="border rounded-md p-3 h-48 overflow-y-auto bg-[var(--panel)]"
        style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
      >
        {messages.filter(m => m.role !== 'system').length === 0 ? (
          <div className="text-sm text-secondary-text">
            {persona === 'talent' ? 'Ask anything about your tasks, portfolio, or messages.' : 'Ask anything about your orders, bookings, or messages.'}
          </div>
        ) : (
          <ul className="space-y-2">
            {messages.filter(m => m.role !== 'system').map((m, idx) => (
              <li key={idx} className={m.role === 'user' ? 'text-sm' : 'text-sm'}>
                <span className={m.role === 'user' ? 'font-medium' : 'text-secondary-text'}>
                  {m.role === 'user' ? 'You' : 'Assistant'}:
                </span>
                {' '}
                <span>{m.content}</span>
              </li>
            ))}
            {loading && (
              <li className="text-sm text-secondary-text">Assistant is typing…</li>
            )}
          </ul>
        )}
      </div>
      {/* Quick action prompts (only for logged-in users) */}
      {loggedIn && (
        <div className="mt-2 flex flex-wrap gap-2">
          {suggestions.map((s, idx) => (
            <button
              key={idx}
              type="button"
              className="btn btn-outline text-xs"
              onClick={() => applySuggestion(s)}
              disabled={loading}
              aria-label={`Try prompt: ${s}`}
              title={s}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      <form onSubmit={sendMessage} className="mt-2 flex gap-2">
        <input
          type="text"
          placeholder="Type a message…"
          className="flex-1 border rounded-md px-3 py-2"
          style={{ borderColor: 'var(--border)' }}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading}
        />
        <button
          type="button"
          className="btn btn-outline"
          onClick={startVoice}
          disabled={loading || !voiceSupported}
          title={voiceSupported ? (voiceActive ? 'Listening…' : 'Voice input') : 'Voice not supported'}
          aria-label="Voice input"
        >
          {voiceActive ? '🎙️…' : '🎙️'}
        </button>
        <button type="submit" className="btn" disabled={loading}>
          {loading ? 'Sending…' : 'Send'}
        </button>
      </form>
      {error && <div className="text-red-500 text-xs mt-2">{error}</div>}
    </div>
  );
}
